import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Calendar,
  Event as CalendarEvent,
  dayjsLocalizer,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { AbsenceTableCard } from "./Absence.styles";
import { AbsenceEntry } from "../../../types/absence";
import { useAppDispatch } from "../../../hooks/StoreHooks";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppDispatch } from "../../../store/store";
import { handleRequest } from "../../../store/utils/handleRequest";
import { absenceApi } from "../../../api/absenceApi";
import i18n from "../../../i18n";

const localizer = dayjsLocalizer(dayjs);

interface AbsenceCalendarProps {
  employeeUserId?: string;
  serviceModuleId: number;
  getEmployeeName: (employeeId: string) => string;
};

export default function AbsenceCalendar(props: AbsenceCalendarProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const colorPalette = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success?.main ?? "#4caf50",
      theme.palette.info?.main ?? "#29b6f6",
      theme.palette.warning?.main ?? "#fb8c00",
      theme.palette.error?.main ?? "#e53935",
      "#7E57C2",
      "#26C6DA",
      "#EC407A",
      "#8D6E63",
    ],
    [theme]
  );

  const [absences, setAbsences] = useState<AbsenceEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dateRange, setDateRange] = useState<{ startDate: Dayjs | null, endDate: Dayjs | null }>({
    startDate: dayjs().startOf('month'),
    endDate: dayjs().endOf('month'),
  });

  useEffect(() => {
    setIsLoading(true);
    const load = async () => {
      const result = await dispatch(
        fetchAbsencesThunk({
          serviceModuleId: props.serviceModuleId,
          employeeUserId: props.employeeUserId,
          startDate: dateRange.startDate ? dateRange.startDate.format("YYYY-MM-DD") : undefined,
          endDate: dateRange.endDate ? dateRange.endDate.format("YYYY-MM-DD") : undefined,
        })
      );

      setIsLoading(false);

      if (fetchAbsencesThunk.fulfilled.match(result)) {
        setAbsences(result.payload);
        return result.payload;
      } else {
        setAbsences([])
      }
    };

    load();
  }, [dateRange, props.employeeUserId]);

  const employeeLegend = useMemo(() => {
    const employeeMap = new Map();

    absences.forEach((absence) => {
      const id = absence.employeeUserId;

      if (id && !employeeMap.has(id)) {
        employeeMap.set(id, {
          id,
          absenceReason: absence.absenceReason,
          label: absence.employeeName,
        });
      }
    });

    return Array.from(employeeMap.values()).map((employeeMap, index) => ({
      ...employeeMap,
      color: colorPalette[index % colorPalette.length],
    }));

  }, [absences, colorPalette, props.getEmployeeName]);

  const events = useMemo<CalendarEvent[]>(() => {
    const approvedAbsences = absences.filter(
      (absence) => absence.absenceApproval === "Approved"
    );

    return approvedAbsences.map((absence) => {
      const startDate = dayjs(absence.startTimestamp).toDate();
      const endDate = dayjs(absence.endTimestamp).add(1, "minute").toDate();

      return {
        title: `${t(
          `serviceModulesPage.absencePage.reason.${absence.absenceReason?.toLocaleLowerCase()}`,
          { defaultValue: absence.absenceReason }
        )} · ${props.getEmployeeName(absence.employeeUserId)}`,
        allDay: false,
        start: startDate,
        end: endDate,
        resource: absence,
      };
    });
  }, [absences, props.getEmployeeName, t]);

  const eventPropGetter = (_event: CalendarEvent) => {
    const absence = _event.resource as AbsenceEntry | undefined;
    const employeeId = absence?.employeeUserId ?? "";
    const legendEntry = employeeLegend.find((entry) => entry.id === employeeId);
    const backgroundColor =
      legendEntry?.color ?? theme.palette.primary.main;

    return {
      style: {
        backgroundColor,
        borderColor: backgroundColor,
        color: theme.palette.getContrastText(backgroundColor),
        borderRadius: theme.shape.borderRadius,
        opacity: 0.9,
      },
    };
  };

  const handleDateRangeChanged = (range: Date[] | { start: Date; end: Date }) => {
    if (!Array.isArray(range)) {
      setDateRange({
        startDate: dayjs(range.start),
        endDate: dayjs(range.end),
      });
    } else {
      const [start, end] = [range[0], range.length > 1 ? range[range.length - 1] : range[0]];
      setDateRange({
        startDate: dayjs(start),
        endDate: dayjs(end),
      });
    }
  };

  return (
    <AbsenceTableCard>
      <Typography variant="h6">
        {t("serviceModulesPage.absencePage.calendar.title")}
      </Typography>

      {employeeLegend.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2 }}>
          {employeeLegend.map((entry) => (
            <Stack
              key={entry.id}
              direction="row"
              sx={{ minWidth: 120, alignItems: "center", gap: 1 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                }}
              />
              <Typography variant="body2">
                {entry.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {isLoading && <CircularProgress />}
      {!isLoading && events.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {t("serviceModulesPage.absencePage.calendar.empty")}
        </Typography>
      )}
      <Box sx={{ height: 500 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={["month"]}
          culture={i18n.language}
          messages={{
            previous: t("serviceModulesPage.calendarPage.calendarPrevious"),
            next: t("serviceModulesPage.calendarPage.calendarNext"),
            today: t("serviceModulesPage.calendarPage.calendarToday"),
          }}
          eventPropGetter={eventPropGetter}
          onRangeChange={handleDateRangeChanged}
          popup
        />
      </Box>
    </AbsenceTableCard>
  );
};

const fetchAbsencesThunk = createAsyncThunk<
  AbsenceEntry[],
  {
    serviceModuleId: number;
    employeeUserId?: string;
    startDate?: string;
    endDate?: string;
  },
  { dispatch: AppDispatch }
>(
  "absence/fetch",
  async ({ serviceModuleId, ...parameters }, { dispatch }) => {
    const result = await handleRequest(
      dispatch,
      "indirect",
      () => absenceApi.getCompanyAbsences(serviceModuleId, parameters)
    );

    return result.success() && result.result
      ? result.result
      : [];
  }
);
